import React from 'react';

/**
 * RiskVerdict - the two-number risk model UI.
 *
 * Replaces a single confusing "%" with:
 *  - a plain-English STATUS (Clear / Watch / At risk / Critical)
 *  - SEVERITY (how bad the worst flagged item is) and EXPOSURE (how much / how visible)
 *  - a recommended ACTION.
 *
 * Reads scan.results ({ status, severity, exposure, recommendedAction, flaggedPosts }).
 * `compact` renders a small pill (for table rows); otherwise a full card.
 */
const STATUS = {
  clear: { label: 'Clear', pill: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300 border-green-200', dot: 'bg-green-500', bar: 'bg-green-500' },
  watch: { label: 'Watch', pill: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300 border-yellow-200', dot: 'bg-yellow-500', bar: 'bg-yellow-500' },
  at_risk: { label: 'At risk', pill: 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300 border-orange-200', dot: 'bg-orange-500', bar: 'bg-orange-500' },
  critical: { label: 'Critical', pill: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300 border-red-200', dot: 'bg-red-500', bar: 'bg-red-500' },
};

// Back-compat: derive a status if an older scan only has a numeric riskScore.
function deriveStatus(results) {
  if (results?.status) return results.status;
  const flagged = results?.flaggedPosts || 0;
  const sev = results?.severity ?? results?.riskScore ?? 0;
  if (!flagged) return 'clear';
  if (sev >= 85) return 'critical';
  if (sev >= 60) return 'at_risk';
  return 'watch';
}

export function RiskVerdict({ results, compact = false }) {
  const statusKey = deriveStatus(results);
  const s = STATUS[statusKey] || STATUS.clear;
  const severity = Math.round(results?.severity ?? results?.riskScore ?? 0);
  const exposure = Math.round(results?.exposure ?? 0);

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${s.pill}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
        {s.label}
        {statusKey !== 'clear' && <span className="opacity-70">· sev {severity}</span>}
      </span>
    );
  }

  const Metric = ({ label, value, hint }) => (
    <div className="flex-1">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className="text-lg font-semibold text-foreground">{value}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${s.bar}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </div>
  );

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-3 mb-4">
        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-semibold ${s.pill}`}>
          <span className={`w-2 h-2 rounded-full ${s.dot}`} />
          {s.label}
        </span>
        <span className="text-sm text-muted-foreground">{results?.flaggedPosts || 0} flagged</span>
      </div>

      <div className="flex gap-6 mb-4">
        <Metric label="Severity" value={severity} hint="How bad the worst flagged item is" />
        <Metric label="Exposure" value={exposure} hint="How much risky content & reach" />
      </div>

      {results?.recommendedAction && (
        <div className="rounded-lg bg-muted border border-border p-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Recommended action</p>
          <p className="text-sm text-foreground">{results.recommendedAction}</p>
        </div>
      )}
    </div>
  );
}

export default RiskVerdict;
